import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
// ThemeProvider lives under @mui/material/styles, not the top-level
// @mui/material barrel — import it from there directly rather than through
// babel-plugin-import's subpath rewrite, which assumes a 1:1 name-to-file
// mapping that doesn't hold for the styles re-exports.
import { ThemeProvider } from '@mui/material/styles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { muiTheme } from '../theme/muiTheme';
import logo from '@/assets/logo.png';

const emailRules = [
  (v) => !!v || 'E-mail is required',
  (v) => /.+@.+\..+/.test(v) || 'E-mail must be valid',
];

const passwordRules = [
  (v) => !!v || 'Password is required',
  (v) => (v && v.length >= 8) || 'Password must be 8 characters long',
];

function firstError(rules, value) {
  for (const rule of rules) {
    const result = rule(value);
    if (result !== true) {
      return result;
    }
  }
  return '';
}

export default function LoginView({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const emailError = touched ? firstError(emailRules, email) : '';
  const passwordError = touched ? firstError(passwordRules, password) : '';

  const login = () => {
    setTouched(true);

    if (firstError(emailRules, email) || firstError(passwordRules, password)) {
      return;
    }

    setLoading(true);

    onLogin({ email, password }).then(
      (response) => {
        setLoading(false);
        setShowPassword(false);
        console.log(response);
      },
      (error) => {
        setLoading(false);
        console.error(error);
      }
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <Card sx={{ width: 400 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Box component="img" src={logo} alt="logo" sx={{ width: 400, objectFit: 'contain' }} />
          </Box>

          <Divider />

          <LinearProgress
            color="primary"
            variant="indeterminate"
            sx={{ visibility: loading ? 'visible' : 'hidden' }}
          />

          <CardContent sx={{ px: 3 }}>
            <TextField
              fullWidth
              margin="normal"
              label="E-mail"
              value={email}
              error={!!emailError}
              helperText={emailError}
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              error={!!passwordError}
              helperText={passwordError}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  login();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              color="primary"
              variant="contained"
              fullWidth
              size="large"
              sx={{ mt: 2 }}
              onClick={login}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  login();
                }
              }}
            >
              Kiosk Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
